import { FC, useState } from 'react';
import { useForm } from 'react-hook-form';
import { PencilIcon, CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useUpdatePastPaper } from '@/api/useAdminPastPapers';

interface PastPaperTitleEditorProps {
  pastPaperId: string;
  currentTitle: string | null;
}

interface FormValues {
  title: string;
}

const PastPaperTitleEditor: FC<PastPaperTitleEditorProps> = ({ pastPaperId, currentTitle }) => {
  const [isEditing, setIsEditing] = useState(false);
  const { mutate: updatePastPaper, isPending } = useUpdatePastPaper();

  const displayValue = currentTitle || 'Untitled';

  const { register, handleSubmit, reset } = useForm<FormValues>({
    defaultValues: {
      title: currentTitle || '',
    },
  });

  const handleStartEdit = () => {
    reset({ title: currentTitle || '' });
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    reset();
  };

  const onSubmit = (data: FormValues) => {
    const trimmed = data.title.trim();

    if (!trimmed) {
      handleCancel();
      return;
    }

    // Don't update if title hasn't changed
    if (trimmed === currentTitle) {
      setIsEditing(false);
      return;
    }

    updatePastPaper(
      {
        pastPaperId,
        updates: { title: trimmed },
      },
      {
        onSuccess: () => {
          setIsEditing(false);
        },
      }
    );
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      handleCancel();
    }
  };

  if (isEditing) {
    return (
      <form onSubmit={handleSubmit(onSubmit)} className="flex items-center gap-2">
        <input
          {...register('title')}
          type="text"
          placeholder="Enter paper title"
          className="flex-1 px-3 py-1.5 text-gray-900 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary text-sm"
          disabled={isPending}
          onKeyDown={handleKeyDown}
          autoFocus
        />
        <button
          type="submit"
          disabled={isPending}
          className="p-1.5 text-green-600 hover:text-green-700 disabled:opacity-50"
          title="Save"
        >
          <CheckIcon className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={handleCancel}
          disabled={isPending}
          className="p-1.5 text-gray-400 hover:text-gray-600 disabled:opacity-50"
          title="Cancel"
        >
          <XMarkIcon className="w-4 h-4" />
        </button>
      </form>
    );
  }

  return (
    <div className="flex items-center gap-2 group">
      <span className="text-gray-900">{displayValue}</span>
      <button
        onClick={handleStartEdit}
        className="p-1 text-gray-400 hover:text-gray-600 opacity-0 group-hover:opacity-100 transition-opacity"
        title="Edit title"
      >
        <PencilIcon className="w-3.5 h-3.5" />
      </button>
    </div>
  );
};

export default PastPaperTitleEditor;
